import {
    WorkspaceEntityType,
    WorkspaceFolderTabProps,
} from "features/data-management/workspace/types"
import { WorkspaceSidePanelTab } from "./WorkspaceSidePanelTab"
import { Folder } from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectDropdownsOpen,
    selectFilteredTagIds,
    selectIdRenaming,
    setDropdownOpen,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useCallback, useMemo, useState, useRef } from "react"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { createContextMenuSections } from "features/data-management/workspace/module"
import { WorkspaceTreeMaxDepth } from "features/data-management/workspace/constants"
import classNames from "classnames"
import { useWorkspaceFolderDragAndDrop } from "../../useWorkspaceFolderDragAndDrop"
import { setActiveContextMenu } from "features/popups/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { useRouter } from "pages"

export const WorkspaceFolderTab = ({
    folder,
    categoryKey,
    tagChain,
    isFavorite,
    handleMove,
    handleCreateFolder,
    handleBeginRenaming,
    handleConfirmRenaming,
    handleCancelRenaming,
    handleOpenShareModal,
    handleToggleFavorite,
    handleDelete,
    subFolders,
    shouldPopulateChildren,
    depth,
    extraCanDropCondition,
}: WorkspaceFolderTabProps) => {
    const keyPrefix = categoryKey.split("_")[0]
    const type = WorkspaceEntityType.Folder

    const dispatch = useAppDispatch()
    const [isHovering, setIsHovering] = useState(false)
    const { isEditor } = useWorkspacePermissions(folder.id)
    const activeWorkspaceSidePanelContextMenu = useAppSelector(
        selectActiveContextMenu("WorkspaceSidePanel")
    )
    const contextMenuIsForThisFolder =
        activeWorkspaceSidePanelContextMenu?.data.entityId === folder.id
    const idRenaming = useAppSelector(selectIdRenaming())
    const isRenaming = idRenaming === categoryKey
    const canCreateFolder = tagChain.length <= WorkspaceTreeMaxDepth

    const dropdownsOpen = useAppSelector(selectDropdownsOpen())
    const dropdownIsOpen = !!dropdownsOpen[folder.id]

    const contextMenuSections = useMemo(
        () =>
            createContextMenuSections({
                id: folder.id,
                key: categoryKey,
                title: folder.name,
                isFavorite: isFavorite(folder.id),
                onCreateFolder: isEditor && canCreateFolder ? handleCreateFolder : undefined,
                onToggleFavorite: handleToggleFavorite,
                onBeginRenaming: isEditor ? handleBeginRenaming : undefined,
                // onMoveTo: ,
                onOpenShareModal: handleOpenShareModal,
                onDelete: isEditor ? (id, name) => handleDelete(id, name, type) : undefined,
            }),
        [
            folder,
            categoryKey,
            type,
            isFavorite,
            isEditor,
            canCreateFolder,
            handleCreateFolder,
            handleToggleFavorite,
            handleBeginRenaming,
            handleOpenShareModal,
            handleDelete,
        ]
    )

    const filteredTagIds = useAppSelector(selectFilteredTagIds())
    const { dragRef, dropRef, isOver, canDrop, isDragging, anythingIsDragging } =
        useWorkspaceFolderDragAndDrop({
            id: folder.id,
            subFolders,
            isRenaming,
            isEditor,
            handleMove,
            extraCanDropCondition,
        })

    /**
     * @param force
     * If `true`, function will force-show the context menu.
     * If `false`, function will force-hide the context menu.
     * If `undefined`, function will toggle the context menu.
     */
    const handleToggleContextMenu = (force?: boolean) => {
        const shouldHide = (contextMenuIsForThisFolder && !force) || force === false

        if (shouldHide) setIsHovering(false)

        dispatch(
            setActiveContextMenu(
                shouldHide
                    ? undefined
                    : {
                          type: "WorkspaceSidePanel",
                          data: {
                              entityId: folder.id,
                              isFavorite: keyPrefix === "fav",
                              fixedPosition: false,
                          },
                          position: { top: 0, left: 0 },
                      }
            )
        )
    }

    const router = useRouter("/workspace/:workspaceId")
    const handleOpenFolder = useCallback(() => {
        router.navigate({ path: "/explore/ws/*", splat: tagChain.join("/") })
    }, [router, tagChain])

    const folderDragRef = useRef<HTMLDivElement>(null)
    dragRef(folderDragRef)

    const folderDropRef = useRef<HTMLDivElement>(null)
    dropRef(folderDropRef)

    return (
        <>
            <div
                ref={folderDragRef}
                className={classNames("relative", {
                    "opacity-20": (!canDrop && anythingIsDragging) || isDragging,
                })}
            >
                <WorkspaceSidePanelTab
                    title={folder.name}
                    id={categoryKey}
                    icon={Folder}
                    isRenaming={isRenaming}
                    handleConfirmRename={(_, name) => handleConfirmRenaming(folder.id, name, type)}
                    handleCancelRename={handleCancelRenaming}
                    contextMenuSections={contextMenuSections}
                    handleToggleContextMenu={handleToggleContextMenu}
                    isCollapsable={subFolders && subFolders.length > 0 && shouldPopulateChildren}
                    depth={depth}
                    dropdownIsOpen={dropdownIsOpen}
                    toggleDropdown={() =>
                        dispatch(setDropdownOpen({ id: folder.id, isOpen: !dropdownIsOpen }))
                    }
                    isHovering={isHovering}
                    isActive={filteredTagIds.at(-1) === folder.id}
                    draggedItemOverCanDrop={isOver ? canDrop : undefined}
                    handleClick={() => handleOpenFolder()}
                    handleRightClick={(e) =>
                        dispatch(
                            setActiveContextMenu({
                                type: "WorkspaceSidePanel",
                                data: {
                                    entityId: folder.id,
                                    isFavorite: keyPrefix === "fav",
                                    fixedPosition: true,
                                },
                                position: { top: e.clientY, left: e.clientX },
                            })
                        )
                    }
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                />
                {anythingIsDragging && (
                    <div ref={folderDropRef} className="absolute h-6 w-full top-1.5" />
                )}
            </div>

            {shouldPopulateChildren &&
                subFolders &&
                dropdownIsOpen &&
                subFolders
                    .toSorted((a, b) => a.name.localeCompare(b.name))
                    .map((subFolder) => (
                        <WorkspaceFolderTab
                            key={subFolder.id}
                            folder={subFolder}
                            categoryKey={`${keyPrefix}_${subFolder.id}`}
                            tagChain={[...tagChain, subFolder.id]}
                            isFavorite={isFavorite}
                            handleMove={handleMove}
                            handleCreateFolder={handleCreateFolder}
                            handleBeginRenaming={handleBeginRenaming}
                            handleConfirmRenaming={handleConfirmRenaming}
                            handleCancelRenaming={handleCancelRenaming}
                            handleOpenShareModal={handleOpenShareModal}
                            handleToggleFavorite={handleToggleFavorite}
                            handleDelete={handleDelete}
                            subFolders={subFolder.subTags}
                            shouldPopulateChildren={true}
                            depth={depth + 1}
                        />
                    ))}
        </>
    )
}
